import React, { useState, useEffect, useCallback } from "react";
import { Table, Badge, Alert, Spinner, Tabs, Tab, Button } from "react-bootstrap";
import axios from "axios";

/**
 * HiuDashboard — admin panel for ABDM HIU operations.
 *
 * Tabs:
 *   - Consent Requests: list outgoing consent requests and their statuses
 *   - Transactions: list data fetch transactions and decryption status
 *   - Callback Paths: show registered HIU endpoints for reference
 */
function HiuDashboard() {
  const [consentRequests, setConsentRequests] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [callbackPaths, setCallbackPaths] = useState(null);
  const [loadingConsent, setLoadingConsent] = useState(false);
  const [loadingTxn, setLoadingTxn] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchConsentRequests = useCallback(async () => {
    setLoadingConsent(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/abdm/admin/hiu/consent-requests`,
        { headers }
      );
      setConsentRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoadingConsent(false);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoadingTxn(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/abdm/admin/hiu/transactions`,
        { headers }
      );
      setTransactions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoadingTxn(false);
    }
  }, []);

  const fetchCallbackPaths = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/abdm/admin/hiu/callback-paths`,
        { headers }
      );
      setCallbackPaths(data);
    } catch {
      // Optional endpoint
    }
  }, []);

  useEffect(() => {
    fetchConsentRequests();
    fetchTransactions();
    fetchCallbackPaths();
  }, [fetchConsentRequests, fetchTransactions, fetchCallbackPaths]);

  const statusBadge = (status) => {
    const colors = {
      INITIATED: "secondary",
      PENDING: "warning",
      GRANTED: "success",
      DENIED: "danger",
      EXPIRED: "dark",
      REQUESTED: "info",
      DATA_RECEIVED: "primary",
      DECRYPTED: "success",
      FAILED: "danger",
    };
    return <Badge bg={colors[status] || "secondary"}>{status}</Badge>;
  };

  return (
    <div className="p-3">
      <h4 className="mb-3">ABDM HIU Dashboard</h4>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {callbackPaths && (
        <Alert variant="info" className="small py-2 mb-3">
          <strong>Callback URL:</strong> {callbackPaths.base || "Not configured"}
          <br />
          <span className="text-muted">
            on-init: {callbackPaths.hiuConsentOnInitUrl}
            &nbsp;|&nbsp; on-status: {callbackPaths.hiuConsentOnStatusUrl}
            &nbsp;|&nbsp; transfer: {callbackPaths.hiuHealthInfoTransferUrl}
          </span>
        </Alert>
      )}

      <Tabs defaultActiveKey="consent" className="mb-3">
        <Tab eventKey="consent" title={`Consent Requests (${consentRequests.length})`}>
          {loadingConsent ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : consentRequests.length === 0 ? (
            <Alert variant="secondary">No consent requests yet.</Alert>
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Patient ABHA</th>
                  <th>HI Types</th>
                  <th>Status</th>
                  <th>Consent Artefact</th>
                </tr>
              </thead>
              <tbody>
                {consentRequests.map((cr) => (
                  <tr key={cr._id}>
                    <td className="small">
                      {cr.createdAt
                        ? new Date(cr.createdAt).toLocaleString()
                        : "—"}
                    </td>
                    <td className="small">{cr.patientAbha || "—"}</td>
                    <td>
                      {(cr.hiTypes || []).map((ht) => (
                        <Badge key={ht} bg="light" text="dark" className="me-1">
                          {ht}
                        </Badge>
                      ))}
                    </td>
                    <td>{statusBadge(cr.status)}</td>
                    <td className="small">
                      {cr.consentArtefactId ? (
                        <code>{cr.consentArtefactId.slice(0, 24)}…</code>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <div className="d-flex justify-content-end">
            <Button variant="outline-secondary" size="sm" onClick={fetchConsentRequests}>
              Refresh
            </Button>
          </div>
        </Tab>

        <Tab eventKey="transactions" title={`Transactions (${transactions.length})`}>
          {loadingTxn ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : transactions.length === 0 ? (
            <Alert variant="secondary">No transactions yet.</Alert>
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Transaction ID</th>
                  <th>HIP ID</th>
                  <th>Status</th>
                  <th>Entries</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx._id}>
                    <td className="small">
                      {tx.createdAt
                        ? new Date(tx.createdAt).toLocaleString()
                        : "—"}
                    </td>
                    <td className="small">
                      <code>{tx.transactionId?.slice(0, 16)}…</code>
                    </td>
                    <td className="small">{tx.hipId || "—"}</td>
                    <td>{statusBadge(tx.status)}</td>
                    <td>{(tx.entries || []).length}</td>
                    <td className="small text-muted">
                      {tx.errorReason || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <div className="d-flex justify-content-end">
            <Button variant="outline-secondary" size="sm" onClick={fetchTransactions}>
              Refresh
            </Button>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

export default HiuDashboard;
